import * as React from "react";
import { useLayoutEffect, useRef, useState } from "react";
import { theme } from "../../theme";
import styled from "styled-components";
import { CheckmarkIconSmall } from "../atoms/icons/CheckmarkIcons";
import { ClockIconSmall } from "../atoms/icons/ClockIcons";
import { FailedIconSmall } from "../atoms/icons/FailedIcons";
import { InfoIconSmall } from "../atoms/icons/InfoIcons";
import { VerticalSpacer } from "../atoms/Spacers";
import BodySmall from "../atoms/typography/BodySmall";
import { useTranslation } from "react-i18next";
import Small from "../atoms/typography/Small";
import Tooltip from "./Tooltip";

export type StatusModel =
  | "checked"
  | "pending"
  | "failed"
  | "disabled"
  | "info"
  | "other";

interface Props {
  label: string;
  status: StatusModel;
  small?: boolean;
}

const Container = styled.div<{ backgroundcolor: string; small?: boolean }>`
  padding: ${(props) => (props.small ? "2px 4px" : "4px 8px")};
  border-radius: 12px;
  background-color: ${(props) => props.backgroundcolor};
  display: flex;
  align-items: center;
  width: fit-content;
  max-width: 100%;
  overflow: hidden;
`;

const StatusBadge: React.FC<Props> = ({ label, status, small }) => {
  const { t } = useTranslation();

  const getBackgroundColor = (status: string): string => {
    switch (status) {
      case "checked":
        return theme.colors.success20;
      case "pending":
        return theme.colors.warning20;
      case "failed":
        return theme.colors.error20;
      case "disabled":
        return theme.colors.disabled;
      case "info":
        return theme.colors.info20;
      case "other":
        return theme.colors.grey05;
      default:
        return "transparent";
    }
  };

  const getIcon = (status: string): React.ReactNode => {
    switch (status) {
      case "checked":
        return <CheckmarkIconSmall color={textColor} />;
      case "pending":
        return <ClockIconSmall color={theme.colors.warning} />;
      case "failed":
        return <FailedIconSmall color={textColor} />;
      case "disabled":
        return <InfoIconSmall color={theme.colors.text} />;
      case "info":
        return <InfoIconSmall color={theme.colors.info} />;
      case "other":
        return <InfoIconSmall />;
      default:
        return "";
    }
  };

  const getColor = (status: string): string => {
    switch (status) {
      case "checked":
        return theme.colors.success;
      case "pending":
        return theme.colors.warning;
      case "failed":
        return theme.colors.error;
      case "disabled":
        return theme.colors.text;
      case "other":
        return theme.colors.text;
      case "info":
        return theme.colors.info;
      default:
        return "transparent";
    }
  };

  const backgroundColor = getBackgroundColor(status);
  const textColor = getColor(status);

  const truncateStyle: React.CSSProperties = {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  };

  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = textRef.current;
    if (!el) return;

    const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [label]);

  return (
    <Tooltip text={isTruncated ? t(label) : undefined}>
      <Container backgroundcolor={backgroundColor} small={small}>
        {getIcon(status)}
        <VerticalSpacer spacing={0.25} />
        {small ? (
          <Small color={textColor} style={truncateStyle} ref={textRef}>
            {t(label)}
          </Small>
        ) : (
          <BodySmall color={textColor} style={truncateStyle} ref={textRef}>
            {t(label)}
          </BodySmall>
        )}
      </Container>
    </Tooltip>
  );
};

export default StatusBadge;
